import { type Lang, LEVELS, type Level, RANKS, type Rank } from '@dc1/shared';
import type { ContentStructure, ContentTexts, ContentVersion } from '@dc1/shared/content';
import { queryOptions } from '@tanstack/react-query';

export interface AreaView {
  id: string;
  groupId: string;
  mins: Record<Rank, Level>;
  optional: boolean;
  name: string;
  note?: string;
  items: string[];
  examples: Record<'2' | '3' | '4', string>;
  try: string;
  ask: string;
}

export interface GroupView {
  id: string;
  n: number;
  name: string;
  short: string;
  tag: string;
  why: string;
  areas: AreaView[];
}

export interface LevelView {
  value: Level;
  code: string;
  name: string;
  desc: string;
  example: string;
  howTo?: string;
}

export interface ContentView {
  version: ContentVersion;
  structure: ContentStructure;
  groups: GroupView[];
  levels: LevelView[];
  ranks: Record<Rank, { name: string; expect: string }>;
  areaById: Map<string, AreaView>;
}

export function buildView(
  version: ContentVersion,
  structure: ContentStructure,
  texts: ContentTexts,
): ContentView {
  const groups = structure.groups.map((g, i) => {
    const gt = texts.groups[g.id];
    if (!gt) throw new Error(`Missing text for group ${g.id}`);
    return {
      id: g.id,
      n: i + 1,
      ...gt,
      areas: g.areas.map((a) => {
        const at = texts.areas[a.id];
        if (!at) throw new Error(`Missing text for area ${a.id}`);
        return { id: a.id, groupId: g.id, mins: a.mins, optional: a.optional, ...at };
      }),
    };
  });
  return {
    version,
    structure,
    groups,
    levels: LEVELS.map((l) => ({ value: l, code: `L${l}`, ...texts.levels[String(l) as '1'] })),
    ranks: Object.fromEntries(RANKS.map((r) => [r, texts.ranks[r]])) as ContentView['ranks'],
    areaById: new Map(groups.flatMap((g) => g.areas.map((a) => [a.id, a] as const))),
  };
}

/** Content is code-split: the 39 areas in both languages load once, on first use. */
export const contentQuery = (version: string, lang: Lang) =>
  queryOptions({
    queryKey: ['content', version, lang],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<ContentView> => {
      const content = await import('@dc1/shared/content');
      if (!content.isContentVersion(version)) throw new Error(`Unknown content version ${version}`);
      return buildView(version, content.getStructure(version), content.getTexts(version, lang));
    },
  });

/** The newest content, for the sign-in page: before sign-in there is no period to pin a version. */
export const latestContentQuery = (lang: Lang) =>
  queryOptions({
    queryKey: ['content', 'latest', lang],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<ContentView> => {
      const content = await import('@dc1/shared/content');
      const version = content.LATEST_CONTENT_VERSION;
      return buildView(version, content.getStructure(version), content.getTexts(version, lang));
    },
  });
